import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { copyTextToSystemClipboard } from "@excalidraw/excalidraw/clipboard";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { useCallback, useEffect, useState } from "react";

import { useAtom, useAtomValue, useSetAtom } from "../../app-jotai";
import {
  activeSceneAtom,
  activeShareAtom,
  currentUserAtom,
  dashboardDialogAtom,
  workspaceApi,
  workspaceRefreshAtom,
  type SceneMeta,
  type Share,
  type ShareRole,
  type WorkspaceFolder,
} from "../../data/workspace";
import {
  collectSceneFiles,
  imageFileIds,
  loadSceneIntoCanvas,
  toBinaryFileData,
  toStoredScene,
} from "../../data/workspace/scene";

import "./Workspace.scss";

const ALL = "__all__";
const ROOT = "__root__";

const ShareManager = ({
  scene,
  onBack,
}: {
  scene: SceneMeta;
  onBack: () => void;
}) => {
  const [shares, setShares] = useState<Share[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const refresh = useCallback(() => {
    workspaceApi
      .listShares(scene.id)
      .then((r) => setShares(r.shares))
      .catch((e) => setError(e.message));
  }, [scene.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = async (role: ShareRole) => {
    try {
      await workspaceApi.createShare(scene.id, role);
      refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const copy = async (url: string) => {
    await copyTextToSystemClipboard(url);
    setCopied(url);
    window.setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="WorkspaceDialog">
      <button type="button" className="WorkspaceAuth__toggle" onClick={onBack}>
        ← Back to scenes
      </button>
      <h2>Share “{scene.name}”</h2>
      <p className="Workspace__muted">
        Anyone with a link can open this scene. Edit links let them change it;
        read-only links are view-only.
      </p>
      <div className="WorkspaceDashboard__toolbar">
        <FilledButton
          size="medium"
          label="Create read-only link"
          onClick={() => create("view")}
        />
        <FilledButton
          size="medium"
          variant="outlined"
          label="Create edit link"
          onClick={() => create("edit")}
        />
      </div>

      {error && <p className="Workspace__error">{error}</p>}

      {shares.length === 0 ? (
        <p className="Workspace__muted">No share links yet.</p>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
        >
          {shares.map((s) => (
            <div key={s.token} className="WorkspaceComment">
              <div className="WorkspaceComment__head">
                <span className="WorkspaceComment__author">
                  {s.role === "edit" ? "✎ Can edit" : "👁 Read-only"}
                </span>
                <div className="WorkspaceComment__actions">
                  <button type="button" onClick={() => copy(s.url)}>
                    {copied === s.url ? "Copied!" : "Copy link"}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await workspaceApi.deleteShare(s.token);
                      refresh();
                    }}
                  >
                    Revoke
                  </button>
                </div>
              </div>
              <div
                className="WorkspaceComment__text"
                style={{ fontSize: "0.75rem" }}
              >
                {s.url}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DashboardDialog = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const [isOpen, setOpen] = useAtom(dashboardDialogAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const [activeScene, setActiveScene] = useAtom(activeSceneAtom);
  const setActiveShare = useSetAtom(activeShareAtom);
  const [refreshTick, setRefreshTick] = useAtom(workspaceRefreshAtom);

  const [scenes, setScenes] = useState<SceneMeta[]>([]);
  const [folders, setFolders] = useState<WorkspaceFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>(ALL);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState<SceneMeta | null>(null);

  const refresh = useCallback(
    () => setRefreshTick((n) => n + 1),
    [setRefreshTick],
  );

  useEffect(() => {
    if (!isOpen || !currentUser) {
      return;
    }
    setError(null);
    Promise.all([workspaceApi.listScenes(), workspaceApi.listFolders()])
      .then(([s, f]) => {
        setScenes(s.scenes);
        setFolders(f.folders);
      })
      .catch((e) => setError(e.message));
  }, [isOpen, currentUser, refreshTick]);

  if (!isOpen) {
    return null;
  }

  const close = () => {
    setOpen(false);
    setSharing(null);
  };

  const visibleScenes = scenes.filter((s) => {
    if (selectedFolder === ALL) {
      return true;
    }
    if (selectedFolder === ROOT) {
      return s.folderId == null;
    }
    return s.folderId === selectedFolder;
  });

  const saveCurrentCanvas = async () => {
    if (!excalidrawAPI) {
      return;
    }
    const name = window.prompt(
      "Name this scene",
      excalidrawAPI.getName() || "Untitled",
    );
    if (name === null) {
      return;
    }
    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const files = excalidrawAPI.getFiles();
      const stored = toStoredScene(elements, appState, files);
      const { scene } = await workspaceApi.createScene({
        name: name.trim() || "Untitled",
        folderId:
          selectedFolder === ALL || selectedFolder === ROOT
            ? null
            : selectedFolder,
        elements: stored.elements,
        appState: stored.appState,
      });
      const sceneFiles = collectSceneFiles(elements, files);
      if (sceneFiles.length) {
        await workspaceApi.uploadFiles(scene.id, sceneFiles);
      }
      setActiveScene({ id: scene.id, name: scene.name });
      setActiveShare(null);
      refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const openScene = async (meta: SceneMeta) => {
    if (!excalidrawAPI) {
      return;
    }
    try {
      const { scene } = await workspaceApi.getScene(meta.id);
      const ids = imageFileIds(scene.elements);
      let files = undefined;
      if (ids.length) {
        const r = await workspaceApi.getFiles(meta.id, ids);
        files = r.files.map(toBinaryFileData);
      }
      loadSceneIntoCanvas(excalidrawAPI, scene, files);
      setActiveScene({ id: scene.id, name: scene.name });
      setActiveShare(null);
      close();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const renameScene = async (meta: SceneMeta) => {
    const name = window.prompt("Rename scene", meta.name);
    if (name === null || !name.trim()) {
      return;
    }
    await workspaceApi.updateScene(meta.id, { name: name.trim() });
    if (activeScene?.id === meta.id) {
      setActiveScene({ id: meta.id, name: name.trim() });
    }
    refresh();
  };

  const deleteScene = async (meta: SceneMeta) => {
    if (!window.confirm(`Delete “${meta.name}”? This cannot be undone.`)) {
      return;
    }
    await workspaceApi.deleteScene(meta.id);
    if (activeScene?.id === meta.id) {
      setActiveScene(null);
    }
    refresh();
  };

  const moveScene = async (meta: SceneMeta, folderId: string) => {
    await workspaceApi.updateScene(meta.id, {
      folderId: folderId === ROOT ? null : folderId,
    });
    refresh();
  };

  const createFolder = async () => {
    const name = window.prompt("New folder name", "Untitled folder");
    if (name === null || !name.trim()) {
      return;
    }
    await workspaceApi.createFolder(name.trim());
    refresh();
  };

  const deleteFolder = async (folder: WorkspaceFolder) => {
    if (
      !window.confirm(
        `Delete folder “${folder.name}”? Scenes inside it are kept and moved out.`,
      )
    ) {
      return;
    }
    await workspaceApi.deleteFolder(folder.id);
    if (selectedFolder === folder.id) {
      setSelectedFolder(ALL);
    }
    refresh();
  };

  return (
    <Dialog size={800} onCloseRequest={close} title="My scenes">
      {!currentUser ? (
        <div className="WorkspaceDialog">
          <p>You need to sign in to access your workspace.</p>
        </div>
      ) : sharing ? (
        <ShareManager scene={sharing} onBack={() => setSharing(null)} />
      ) : (
        <div className="WorkspaceDashboard">
          <div className="WorkspaceDashboard__sidebar">
            <button
              type="button"
              className={`WorkspaceDashboard__folder ${
                selectedFolder === ALL
                  ? "WorkspaceDashboard__folder--active"
                  : ""
              }`}
              onClick={() => setSelectedFolder(ALL)}
            >
              All scenes
            </button>
            <button
              type="button"
              className={`WorkspaceDashboard__folder ${
                selectedFolder === ROOT
                  ? "WorkspaceDashboard__folder--active"
                  : ""
              }`}
              onClick={() => setSelectedFolder(ROOT)}
            >
              No folder
            </button>
            {folders.map((f) => (
              <div
                key={f.id}
                className={`WorkspaceDashboard__folder ${
                  selectedFolder === f.id
                    ? "WorkspaceDashboard__folder--active"
                    : ""
                }`}
                onClick={() => setSelectedFolder(f.id)}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  📁 {f.name}
                </span>
                <button
                  type="button"
                  className="WorkspaceDashboard__folderDelete"
                  title="Delete folder"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteFolder(f);
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="WorkspaceDashboard__folder"
              onClick={createFolder}
              style={{ color: "var(--color-primary, #6965db)" }}
            >
              + New folder
            </button>
          </div>

          <div className="WorkspaceDashboard__main">
            <div className="WorkspaceDashboard__toolbar">
              <FilledButton
                size="medium"
                label="Save current canvas here"
                onClick={saveCurrentCanvas}
              />
              <span className="Workspace__muted">
                Signed in as {currentUser.displayName}
              </span>
            </div>

            {error && <p className="Workspace__error">{error}</p>}

            {visibleScenes.length === 0 ? (
              <div className="WorkspaceDashboard__empty">
                No scenes here yet. Draw something, then “Save current canvas”.
              </div>
            ) : (
              <div className="WorkspaceDashboard__grid">
                {visibleScenes.map((s) => (
                  <div
                    key={s.id}
                    className={`WorkspaceSceneCard ${
                      activeScene?.id === s.id
                        ? "WorkspaceSceneCard--active"
                        : ""
                    }`}
                  >
                    <div
                      className="WorkspaceSceneCard__name"
                      onClick={() => openScene(s)}
                      title={s.name}
                    >
                      {s.name}
                    </div>
                    <div
                      className="WorkspaceSceneCard__meta"
                      onClick={() => openScene(s)}
                    >
                      {new Date(s.updatedAt).toLocaleDateString()}
                    </div>
                    <select
                      className="WorkspaceSceneCard__meta"
                      value={s.folderId ?? ROOT}
                      onChange={(e) => moveScene(s, e.target.value)}
                      style={{ marginTop: "0.15rem" }}
                    >
                      <option value={ROOT}>No folder</option>
                      {folders.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                    <div className="WorkspaceSceneCard__actions">
                      <button type="button" onClick={() => openScene(s)}>
                        Open
                      </button>
                      <button type="button" onClick={() => renameScene(s)}>
                        Rename
                      </button>
                      <button type="button" onClick={() => setSharing(s)}>
                        Share
                      </button>
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => deleteScene(s)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
};
